import { useEffect } from 'react';

/**
 * Dispatched on `document` each time this lock engages, so hover popups can
 * close under the overlay (CMS-2082).
 */
export const BODY_SCROLL_LOCK_EVENT = 'mhg:body-scroll-lock';

// Overlays stack and close in either order, so body is restored only when
// the last lock releases, to what the first lock found.
let activeLocks = 0;
let bodyBeforeLock: { overflow: string; paddingRight: string } | null = null;

/**
 * Locks body scroll while `locked` is true, compensating for the removed
 * scrollbar width so the page doesn't shift. Restores on unlock / unmount.
 *
 * Shared by modal primitives (`<Modal>`, `<AsideModal>`, `<SlideUpModal>`).
 * Genuinely cross-component DOM behaviour, hence a shared hook rather than
 * colocated.
 */
export const useBodyScrollLock = (locked: boolean): void => {
  useEffect(() => {
    if (!locked) return;

    if (activeLocks === 0) {
      const { style } = document.body;
      bodyBeforeLock = {
        overflow: style.overflow,
        paddingRight: style.paddingRight,
      };
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;
      style.paddingRight = `${scrollbarWidth}px`;
      document.body.classList.add('overflow-hidden');
      // Inline as well as the class: the header `Sidebar` writes an inline
      // `overflow: auto` on body while closed, which a class rule cannot beat.
      style.overflow = 'hidden';
    }
    activeLocks += 1;
    document.dispatchEvent(new Event(BODY_SCROLL_LOCK_EVENT));

    return () => {
      activeLocks -= 1;
      if (activeLocks > 0 || !bodyBeforeLock) return;
      document.body.style.paddingRight = bodyBeforeLock.paddingRight;
      document.body.classList.remove('overflow-hidden');
      document.body.style.overflow = bodyBeforeLock.overflow;
      bodyBeforeLock = null;
    };
  }, [locked]);
};
